// NOTE: file taken from rental-manager-env
// improvements are welcome!

/*
 * NOTE: any changes done to 'apps/zillow-rental-manager-web/app/client/utils/analyticsUtils.js'
 * must be mirrored to respective functions/methods in this file until `ZRMW` can start consuming
 */
import * as zanalytics from '@zillow/universal-analytics';
import * as Env from '@zg-rentals/rental-platform-config';

import MultiAnalyticsModule from './MultiAnalyticsModule';
import brands from './constants/brands';
import customDimensions from './constants/customDimensions';
import gaTrackers from './constants/gaTrackers';

import type { IEventOverrideOptions, IOverrideOptions } from '@zillow/universal-analytics';
import type { ClickstreamEvent, GaEvent, GaEventAdditionalParams } from './types';
import { useEffect } from 'react';

const ACCEPTED_HOST_APPS = ['ios', 'android'];
const TRACK_UA_EVENT_ERROR_MESSAGE =
  'ERROR @trackUAEvent: properties.envelope or properties.clickstream_trigger or properties.semantic is undefined';

function getGA() {
  if (typeof window !== 'undefined' && window !== null && typeof window.ga !== 'undefined') {
    return window.ga;
  }

  return false;
}

function getMaInstance() {
  return MultiAnalyticsModule.getInstance();
}

export default {
  /**
   * NOTE: `GAEventV2` SHOULD ONLY BE CALLED IF YOU ARE DEALING WITH MIGRATION EVENTS
   * ELSE, CALL `trackUAEvent` for both net new csEvents and net new csPageView
   */
  GAEventV2(gaEventObj: GaEvent, additionalParams?: GaEventAdditionalParams) {
    if (getGA()) {
      const category = additionalParams?.category || gaEventObj.category;
      const action = additionalParams?.action || gaEventObj.action;
      const label = additionalParams?.label || gaEventObj.label;
      const value = additionalParams?.value || gaEventObj.value || '';

      // use as an adaptor for now
      // TODO: remove track.GAEventV2 and update instances to use this.trackEvent
      this.trackEvent(category, action, label, value, additionalParams?.clickstreamEvent);

      // clear dynamic dimensions
      this.clearCustomDimension(customDimensions.ua.zrm.EVENT_CONTENT);
    }
  },

  /**
   * NOTE: `trackEvent` SHOULD ONLY BE CALLED IF YOU ARE DEALING WITH MIGRATION EVENTS
   * ELSE, CALL `trackUAEvent` for both net new csEvents and net new csPageView
   */
  trackEvent(category: string, action: string, label: string, value: string, clickstreamEvent?: ClickstreamEvent) {
    // @ts-expect-error
    getMaInstance().getMa().trackEvent({
      category,
      action,
      label,
      value,
      clickstreamEvent,
    });
  },
  clearCustomDimension(dimension: string) {
    if (!dimension) {
      // appLogger.logError({
      //   message: 'ERROR @clearCustomDimension: dimension must be defined',
      //   context: 'analyticsUtils.clearCustomDimension',
      // });
      return;
    }

    // @ts-ignore
    getMaInstance().getMa().setCustomDimensions(
      {
        name: dimension,
        value: null,
      },
      // @ts-ignore
      [getMaInstance().getTrackerIds().ua],
    );
  },

  /**
   * NOTE: `trackPageView` SHOULD ONLY BE CALLED IF YOU ARE DEALING WITH MIGRATION EVENTS
   * ELSE, CALL `trackUAEvent` for both net new csEvents and net new csPageView
   */
  // this corresponds to the send command in ua, and the comet_page_view_event in gtm
  trackPageView(path: string, clickstreamEvent?: ClickstreamEvent) {
    // ContentSquare tracking
    /* eslint-disable no-underscore-dangle */
    if (typeof window !== 'undefined' && window !== null) {
      // @ts-ignore
      window._uxa = window._uxa || [];
      // @ts-ignore
      window._uxa.push(['trackPageview', path]);
    }
    if (!path) {
      // appLogger.logError({
      //   message: 'ERROR @trackPageView: path must be defined',
      //   context: 'analyticsUtils.trackPageView',
      // });
      return;
    }

    const trackerArgs = { path, clickstreamEvent };
    getMaInstance()
      ?.getMa()
      // @ts-ignore
      ?.trackPageView(trackerArgs, [], () => {});
  },

  /**
   * NOTE: FOR ALL NET NEW CS EVENTS AND CS PAGEVIEW, CALL `trackUAEvent`
   */
  trackUAEvent(properties: ClickstreamEvent, options?: IEventOverrideOptions | IOverrideOptions) {
    if (!properties?.envelope || !properties?.clickstream_trigger || !properties?.semantic) {
      // eslint-disable-next-line no-console
      console.error(TRACK_UA_EVENT_ERROR_MESSAGE);

      return;
    }

    if (!properties?.envelope?.event_client_start_dtm) {
      properties.envelope.event_client_start_dtm = new Date().toISOString();
    }

    zanalytics.event(properties, options);
  },

  // trackerType here is going to be a brand or gtm
  // @ts-ignore
  setCustomDimension(value, dimension, trackerType) {
    if (!value) {
      // appLogger.logError({
      //   message: 'ERROR @setCustomDimension: value must be defined',
      //   context: 'analyticsUtils.setCustomDimension',
      // });
      return;
    }

    if (!dimension) {
      // appLogger.logError({
      //   message: 'ERROR @setCustomDimension: dimension must be defined',
      //   context: 'analyticsUtils.setCustomDimension',
      // });
      return;
    }

    if (!trackerType) {
      // appLogger.logError({
      //   message: 'ERROR @setCustomDimension: trackerType must be defined',
      //   context: 'analyticsUtils.setCustomDimension',
      // });
      return;
    }

    let trackerId: string | undefined;

    switch (trackerType) {
      case 'zillow':
        trackerId = getMaInstance()?.getTrackerIds().ua;
        break;

      case gaTrackers.gtm:
        trackerId = getMaInstance()?.getTrackerIds().gtm;
        break;

      case gaTrackers.dsp:
        trackerId = getMaInstance()?.getTrackerIds().dsp;
        break;

      default:
        break;
    }

    getMaInstance()
      ?.getMa()
      ?.setCustomDimensions(
        // @ts-ignore
        {
          name: dimension,
          value,
        },
        [trackerId],
        () => {},
      );
  },

  setMobileAppCustomDimension() {
    if (typeof window !== 'undefined') {
      const searchParams = new URLSearchParams(window.location.search);
      const hostApp = searchParams.get('hostApp') || '';

      this.setCustomDimension(
        ACCEPTED_HOST_APPS.includes(hostApp).toString(),
        customDimensions.gtm.IS_MOBILE_APP,
        gaTrackers.gtm,
      );
    }
  },

  // initializes MA on the client side
  useMultiAnalytics(brand = brands.zillow, zgEnableGa = false, drymode = false) {
    // useEffect does not run on server, so we have access to window\
    useEffect(() => {
      const multiAnalyticsSettings = {
        brand,
        drymode,
        isProd: Env.getCurrentEnvForClientSide() === Env.PRODUCTION,
        isWebPerformanceTest: false,
        ZRM_ENABLE_GA: zgEnableGa,
      };

      MultiAnalyticsModule.initialize(multiAnalyticsSettings);

      // MA should only be initialized once
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
  },
};
